import type { PendulumParams } from './types.ts';

interface Point { x: number; y: number }
interface ScreenPt { px: number; py: number }

interface DrawArgs {
  currentNodes: Point[];
  state: number[];
  system: string;
  params: PendulumParams;
  diverged: boolean;
}

export interface CanvasRenderer {
  draw(args: DrawArgs): void;
  resize(w: number, h: number): void;
  clearTrail(): void;
  seedTrail(nodes: Point[], system: string): void;
  addSegments(frames: Point[][], system: string): void;
}

const GOLD = 'rgba(169,130,74,1)';
const GOLD_BRIGHT = 'rgba(197,162,103,1)';
const BLUE = 'rgba(44,70,131,1)';

function isSculptureLike(system: string): boolean {
  return system === 'sculpture' || system === 'resonantPendulum';
}

export function createCanvasRenderer(canvas: HTMLCanvasElement): CanvasRenderer {
  const ctx = canvas.getContext('2d')!;
  let cw = canvas.width;
  let ch = canvas.height;

  // persistent offscreen layer: trail segments are painted once and never redrawn
  const trailCanvas = document.createElement('canvas');
  const tctx = trailCanvas.getContext('2d')!;
  const tempCanvas = document.createElement('canvas');
  const xctx = tempCanvas.getContext('2d')!;

  let maxReach = 2;                 // running max of observed extent (physics units)
  let prevArmScale: number | null = null;
  let lastTips: ScreenPt[] | null = null;       // last drawn tip in pixels
  let lastPhysics: Point[] | null = null;       // last tip positions in physics coords (for resize remap)
  let trackedIndices: number[] = [];

  function armScaleFor(w: number, h: number): number {
    return Math.min(110, (Math.min(w, h) / 2 - 26) / (maxReach * 1.15));
  }

  function transform() {
    const armScale = armScaleFor(cw, ch);
    const cx = cw / 2;
    const cy = ch / 2;
    const tc = (x: number, y: number): ScreenPt => ({ px: cx + x * armScale, py: cy - y * armScale });
    return { armScale, cx, cy, tc };
  }

  function resize(w: number, h: number) {
    const oldW = cw, oldH = ch;
    cw = w; ch = h;
    canvas.width = w;
    canvas.height = h;

    // Preserve the existing trail: capture it at the old resolution, then affine-remap
    // onto the resized layer. The mapping (physics -> pixel) keeps a single scale k so a
    // setTransform remap + re-seeded tip positions keep everything aligned.
    if (oldW > 0 && oldH > 0 && lastPhysics && lastPhysics.length > 0) {
      tempCanvas.width = oldW;
      tempCanvas.height = oldH;
      xctx.clearRect(0, 0, oldW, oldH);
      xctx.drawImage(trailCanvas, 0, 0);

      trailCanvas.width = w;
      trailCanvas.height = h;
      const oldA = armScaleFor(oldW, oldH);
      const newA = armScaleFor(w, h);
      const k = oldA > 0 ? newA / oldA : 1;
      tctx.setTransform(k, 0, 0, k, w / 2 - k * oldW / 2, h / 2 - k * oldH / 2);
      tctx.drawImage(tempCanvas, 0, 0);
      tctx.setTransform(1, 0, 0, 1, 0, 0);

      const { tc } = transform();
      lastTips = lastPhysics.map((p) => tc(p.x, p.y));
      prevArmScale = newA;
    } else {
      trailCanvas.width = w;
      trailCanvas.height = h;
      lastTips = null;
      trackedIndices = [];
      prevArmScale = null;
    }
    tempCanvas.width = w;
    tempCanvas.height = h;
  }

  // if the reach has grown, shrink the existing trail layer about the centre so it stays aligned
  function maybeRescale(): void {
    const { armScale, cx, cy } = transform();
    if (prevArmScale === null) { prevArmScale = armScale; return; }
    if (Math.abs(armScale - prevArmScale) > 1e-9) {
      const k = armScale / prevArmScale; // <= 1
      if (tempCanvas.width !== cw || tempCanvas.height !== ch) {
        tempCanvas.width = cw;
        tempCanvas.height = ch;
      }
      xctx.clearRect(0, 0, cw, ch);
      xctx.drawImage(trailCanvas, 0, 0);
      tctx.clearRect(0, 0, cw, ch);
      tctx.drawImage(tempCanvas, cx - cx * k, cy - cy * k, cw * k, ch * k);
      if (lastTips) lastTips = lastTips.map((p) => ({ px: cx + (p.px - cx) * k, py: cy + (p.py - cy) * k }));
      prevArmScale = armScale;
    }
  }

  function indicesFor(nodeCount: number, sculpture: boolean): number[] {
    if (sculpture) {
      const out: number[] = [];
      for (let i = 1; i < nodeCount; i++) out.push(i);
      return out;
    }
    return [nodeCount - 1];
  }

  function clearTrail(): void {
    tctx.clearRect(0, 0, cw, ch);
    lastTips = null;
    lastPhysics = null;
    trackedIndices = [];
    maxReach = 2;
    prevArmScale = null;
  }

  function absorbReach(nodes: Point[]): void {
    for (const p of nodes) {
      const ext = Math.max(Math.abs(p.x), Math.abs(p.y));
      if (ext > maxReach) maxReach = ext;
    }
  }

  function seedTrail(nodes: Point[], system: string): void {
    absorbReach(nodes);
    maybeRescale();
    const { tc } = transform();
    trackedIndices = indicesFor(nodes.length, isSculptureLike(system));
    lastPhysics = trackedIndices.map((i) => nodes[i]);
    lastTips = trackedIndices.map((i) => tc(nodes[i].x, nodes[i].y));
  }

  function paintSegment(idx: number, nodeCount: number, sculpture: boolean): void {
    if (sculpture) {
      const t = nodeCount > 1 ? idx / (nodeCount - 1) : 0;
      const alpha = 0.40 + t * 0.45;
      tctx.strokeStyle = `rgba(44,70,131,${alpha.toFixed(2)})`;
      tctx.lineWidth = 0.8 + t * 1.0;
    } else {
      tctx.strokeStyle = 'rgba(44,70,131,0.78)';
      tctx.lineWidth = 1.6;
    }
    tctx.lineCap = 'round';
    tctx.lineJoin = 'round';
    tctx.stroke();
  }

  function addSegments(frames: Point[][], system: string): void {
    if (frames.length === 0) return;
    const sculpture = isSculptureLike(system);
    const nodeCount = frames[0].length;
    for (const f of frames) absorbReach(f);
    maybeRescale();
    const { tc } = transform();

    const indices = indicesFor(nodeCount, sculpture);
    if (lastTips === null || trackedIndices.length !== indices.length) {
      trackedIndices = indices;
      lastPhysics = indices.map((i) => frames[0][i]);
      lastTips = indices.map((i) => tc(frames[0][i].x, frames[0][i].y));
    }

    for (const f of frames) {
      for (let k = 0; k < indices.length; k++) {
        const idx = indices[k];
        const prev = lastTips![k];
        const cur = tc(f[idx].x, f[idx].y);
        tctx.beginPath();
        tctx.moveTo(prev.px, prev.py);
        tctx.lineTo(cur.px, cur.py);
        paintSegment(idx, nodeCount, sculpture);
        lastTips![k] = cur;
        lastPhysics![k] = f[idx];
      }
    }
  }

  function draw(args: DrawArgs): void {
    const { currentNodes, state, system, params, diverged } = args;
    ctx.clearRect(0, 0, cw, ch);
    ctx.save();

    // galvanized silver-plated dial face
    const grad = ctx.createRadialGradient(cw / 2, ch / 2, Math.min(cw, ch) * 0.1, cw / 2, ch / 2, Math.max(cw, ch) * 0.75);
    grad.addColorStop(0, 'rgba(248,245,236,1)');
    grad.addColorStop(1, diverged ? 'rgba(224,202,170,1)' : 'rgba(232,224,202,1)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, cw, ch);

    const { tc } = transform();
    ctx.drawImage(trailCanvas, 0, 0);

    if (isSculptureLike(system)) {
      drawSculptureFrame(ctx, state, system, params, tc, cw, ch);
    } else {
      const p0 = tc(0, 0);
      const p1 = tc(currentNodes[0].x, currentNodes[0].y);
      const p2 = tc(currentNodes[1].x, currentNodes[1].y);

      // rods: first in tempered blue, second in gold (the two "hands")
      ctx.beginPath(); ctx.moveTo(p0.px, p0.py); ctx.lineTo(p1.px, p1.py);
      ctx.strokeStyle = 'rgba(90,100,133,0.85)'; ctx.lineWidth = 2.2; ctx.stroke();
      ctx.beginPath(); ctx.moveTo(p1.px, p1.py); ctx.lineTo(p2.px, p2.py);
      ctx.strokeStyle = GOLD; ctx.lineWidth = 2.4; ctx.stroke();

      const br1 = 2 + params.m1 * 2;
      const br2 = 2.5 + params.m2 * 2.5;
      ctx.beginPath(); ctx.arc(p1.px, p1.py, br1, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(110,58,40,0.65)'; ctx.fill();
      ctx.strokeStyle = 'rgba(133,96,49,0.8)'; ctx.lineWidth = 1; ctx.stroke();
      ctx.beginPath(); ctx.arc(p2.px, p2.py, br2, 0, Math.PI * 2);
      ctx.fillStyle = BLUE; ctx.fill();
      ctx.shadowColor = 'rgba(44,70,131,0.45)'; ctx.shadowBlur = 8; ctx.fill();
      ctx.shadowBlur = 0;
      ctx.beginPath(); ctx.arc(p2.px, p2.py, br2 * 0.42, 0, Math.PI * 2);
      ctx.fillStyle = GOLD_BRIGHT; ctx.fill();

      // pivot cap (polished)
      ctx.beginPath(); ctx.arc(p0.px, p0.py, 3, 0, Math.PI * 2);
      ctx.fillStyle = GOLD; ctx.fill();
      ctx.beginPath(); ctx.arc(p0.px, p0.py, 1.2, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,250,240,0.8)'; ctx.fill();

      ctx.font = '500 12px "Cormorant Garamond", Georgia, serif';
      ctx.fillStyle = 'rgba(40,50,79,0.72)';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(`\u03B8\u2081 ${state[0].toFixed(2)}`, 10, ch - 22);
      ctx.fillText(`\u03B8\u2082 ${state[4].toFixed(2)}`, 10, ch - 8);
    }

    if (diverged) {
      ctx.font = '600 10px "Cormorant Garamond", Georgia, serif';
      ctx.fillStyle = 'rgba(110,58,40,0.95)';
      ctx.textAlign = 'right';
      ctx.fillText('DIVERGED', cw - 10, ch - 10);
    }

    ctx.restore();
  }

  return { draw, resize, clearTrail, seedTrail, addSegments };
}

interface Joint { wbx: number; wby: number; tpx: number; tpy: number }

function computeJoints(
  state: number[],
  system: string,
  params: PendulumParams,
): { joints: Joint[]; n: number; masses: number[] } {
  const th: number[] = [];
  const a: number[] = [];
  const b: number[] = [];
  const masses: number[] = [];
  let n: number;

  if (system === 'resonantPendulum') {
    n = 2;
    const L0 = params.rpL0, a0 = params.rpA0, L1 = params.rpL1;
    a.push(a0, L1);
    b.push(L0 - a0, 0);
    masses.push(params.rpM0, params.rpM1);
    th.push(state[0], state[4]);
  } else {
    n = Math.max(1, Math.min(4, Math.floor(params.sculptureN)));
    const r = params.sculptureReduction;
    const W = params.sculptureWeight, L = params.sculptureRod, A = params.sculptureAxle;
    const b0 = L - A;
    let ri = 1;
    for (let i = 0; i < n; i++) {
      a.push(A * ri); b.push(b0 * ri); masses.push(W * ri); ri *= r;
    }
    th.push(state[0], state[2], state[4], state[6]);
  }

  const joints: Joint[] = [];
  let ax = 0, ay = 0;
  for (let i = 0; i < n; i++) {
    const ux = Math.sin(th[i]), uy = -Math.cos(th[i]);
    joints.push({
      wbx: ax + a[i] * ux, wby: ay + a[i] * uy,
      tpx: ax - b[i] * ux, tpy: ay - b[i] * uy,
    });
    ax -= b[i] * ux;
    ay -= b[i] * uy;
  }

  return { joints, n, masses };
}

function drawSculptureFrame(
  ctx: CanvasRenderingContext2D,
  state: number[],
  system: string,
  params: PendulumParams,
  tc: (x: number, y: number) => ScreenPt,
  cw: number, ch: number,
): void {
  const { joints, n, masses } = computeJoints(state, system, params);
  const last = n - 1;

  for (let i = 0; i < n; i++) {
    const jt = joints[i];
    const wp = tc(jt.wbx, jt.wby);
    const tp = tc(jt.tpx, jt.tpy);

    ctx.beginPath(); ctx.moveTo(wp.px, wp.py); ctx.lineTo(tp.px, tp.py);
    ctx.strokeStyle = i === last ? GOLD : 'rgba(90,100,133,0.8)';
    ctx.lineWidth = Math.max(1, n - i + 1);
    ctx.stroke();

    const rad = 2 + Math.cbrt(masses[i]) * 0.6;
    ctx.beginPath(); ctx.arc(wp.px, wp.py, rad, 0, Math.PI * 2);
    ctx.fillStyle = i === last ? BLUE : 'rgba(110,58,40,0.75)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(133,96,49,0.7)';
    ctx.lineWidth = 1; ctx.stroke();
  }

  const pv = tc(0, 0);
  ctx.beginPath(); ctx.arc(pv.px, pv.py, 3, 0, Math.PI * 2);
  ctx.fillStyle = GOLD; ctx.fill();

  ctx.font = '500 12px "Cormorant Garamond", Georgia, serif';
  ctx.fillStyle = 'rgba(40,50,79,0.72)';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  const subs = ['\u2080', '\u2081', '\u2082', '\u2083'];
  for (let i = 0; i < n; i++) {
    const angle = Math.atan2(joints[i].wbx, -joints[i].wby);
    ctx.fillText(`\u03B8${subs[i]} ${angle.toFixed(2)}`, 10, ch - (n - i) * 14 - 8);
  }
}
